import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Tabs, type TabItem } from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import type { AdminRegistryApp } from '@/api/console';
import type { AppDetailTabKey } from './types';
import detail from './AppDetail.module.css';

export interface AppDetailHeaderProps {
    code: string;
    app: AdminRegistryApp;
    tab: AppDetailTabKey;
    onSelectTab: (tab: AppDetailTabKey) => void;
    /** Present only when the caller has already resolved the sync permission
     * (`console.rolemap.sync`) — the button renders whenever this is set. */
    onSync?: () => void;
    /** Present only when the caller has already resolved `console.apps.manage`
     * — same convention as `onSync`. Navigates to the frame-51 edit page. */
    canEdit?: boolean;
    /** EAA-1861: present only when the caller has resolved
     *  `console.apps.sync_users` — same convention as `onSync`/`canEdit`. */
    onSyncUsers?: () => void;
    /** EAA-1878: present only when the caller has resolved
     *  `console.apps.sync_organizations` — same convention as `onSync`/
     *  `onSyncUsers`/`canEdit`. */
    onSyncOrganizations?: () => void;
    /** EAA-1709: actions rendered before the sync/edit buttons — the
     *  "Kiểm tra kết nối" / "Duyệt" pair for an app that is not yet enabled. */
    leadingActions?: ReactNode;
}

// EAA-1817 (Portal 6 frame 47-52, 2026-08-14 design revision): "Tài khoản"/
// "Tổ chức" inserted between "roles" and "connection", per the design's tab
// order.
const TAB_ORDER: AppDetailTabKey[] = [
    'overview',
    'roles',
    'accounts',
    'organizations',
    'connection',
    'audit',
];

/**
 * EAA-1817 (frames 47-51): the identity row + 4-tab bar shared by every tab of
 * `/console/apps/:code`. Status pill reads "Đã duyệt" (never "Đang chạy" —
 * `enabled` is an approval flag, not a heartbeat; the mock's "Đang chạy"
 * predates the fix already shipped for this label in EAA-1814/EAA-1819).
 */
export function AppDetailHeader({
    code,
    app,
    tab,
    onSelectTab,
    onSync,
    canEdit,
    onSyncUsers,
    onSyncOrganizations,
    leadingActions,
}: AppDetailHeaderProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tabLabels: Record<AppDetailTabKey, string> = {
        overview: t('console.appDetail.tabs.overview'),
        roles: t('console.appDetail.tabs.roles'),
        accounts: t('console.appDetail.tabs.accounts'),
        organizations: t('console.appDetail.tabs.organizations'),
        connection: t('console.appDetail.tabs.connection'),
        config: t('console.appDetail.tabs.config'),
        audit: t('console.appDetail.tabs.audit'),
    };

    // EAA-1846 (frame 95): breadcrumb moved to the shared topbar.
    // EAA-1817 (2026-08-14 design fix): this trail was missing its root
    // section label — every sibling page in this folder (`AppsPage`,
    // `AppsCreatePage`, `AppEditPage`) leads with
    // `console.apps.page.breadcrumbParent` ("Ứng dụng & quyền", no `to` — a
    // plain, non-clickable section label per the design), only "Ứng dụng"
    // and the current page differ per screen.
    useConsoleBreadcrumb([
        { label: t('console.apps.page.breadcrumbParent') },
        { label: t('console.appDetail.breadcrumbApps'), to: '/console/apps' },
        { label: app.name },
    ]);

    return (
        <div className={detail.header}>
            <div className={detail.identity}>
                <span className={detail.identityTile} aria-hidden="true">
                    {code.slice(0, 3).toUpperCase()}
                </span>
                <div className={detail.identityBody}>
                    <div className={detail.identityTitleRow}>
                        <h1 className={detail.identityName}>{app.name}</h1>
                        <code className={detail.identityCode}>{app.code}</code>
                        <span
                            className={`${detail.identityStatus} ${
                                app.enabled ? detail.isApproved : detail.isPending
                            }`}
                        >
                            {app.enabled
                                ? t('console.appDetail.statusApproved')
                                : t('console.appDetail.statusPending')}
                        </span>
                    </div>
                    <span className={detail.identityMeta}>
                        {app.web_url}
                        {' · '}
                        {t('console.appDetail.metaAudience')}{' '}
                        <code className={detail.identityMetaCode}>{app.audience}</code>
                    </span>
                </div>
                {(leadingActions || onSync || onSyncUsers || onSyncOrganizations || canEdit) && (
                    <div className={detail.identityActions}>
                        {leadingActions}
                        {onSync && <Button onClick={onSync}>{t('console.appDetail.syncCatalogButton')}</Button>}
                        {onSyncUsers && (
                            <Button onClick={onSyncUsers}>
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    aria-hidden="true"
                                    style={{ marginRight: 7 }}
                                >
                                    <path d="M17 8l4 4-4 4M3 12h18" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="8" cy="8" r="3" />
                                    <path d="M3 20c0-3 2-5 5-5" strokeLinecap="round" />
                                </svg>
                                {t('console.appDetail.syncUsersButton')}
                            </Button>
                        )}
                        {onSyncOrganizations && (
                            <Button onClick={onSyncOrganizations}>
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    aria-hidden="true"
                                    style={{ marginRight: 7 }}
                                >
                                    <path
                                        d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 21v-9h5a1 1 0 0 1 1 1v8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path d="M7 7h2M11 7h0M7 11h2M11 11h0M7 15h2M11 15h0" strokeLinecap="round" />
                                </svg>
                                {t('console.appDetail.syncOrganizationsButton')}
                            </Button>
                        )}
                        {canEdit && (
                            <Button type="primary" onClick={() => navigate(`/console/apps/${code}/edit`)}>
                                {t('console.appDetail.editInfoButton')}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <Tabs<AppDetailTabKey>
                className={detail.tabs}
                items={TAB_ORDER.map(
                    (key): TabItem<AppDetailTabKey> => ({ key, label: tabLabels[key] })
                )}
                activeKey={tab}
                onChange={onSelectTab}
            />
        </div>
    );
}

export default AppDetailHeader;
